import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  MarketGasPriceType,
  MarketGasPriceTypes,
  MarketGasPriceTypesRequestBody,
} from './types';

export const marketGasPriceTypesApi = createApi({
  reducerPath: 'marketGasPriceTypesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/market-gas-price-types`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['MarketGasPriceType', 'MarketGasPriceTypes'],
  endpoints: builder => ({
    getMarketGasPriceTypesI: builder.infiniteQuery<
      MarketGasPriceType[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MarketGasPriceType', 'MarketGasPriceTypes'],
    }),
    getMarketGasPriceTypesById: builder.query<MarketGasPriceTypes, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_result, _error, id) => [
        { type: 'MarketGasPriceTypes', id },
      ],
    }),
    createMarketGasPriceTypes: builder.mutation<
      string,
      MarketGasPriceTypesRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: () => ['MarketGasPriceTypes'],
    }),
    updateMarketGasPriceTypes: builder.mutation<
      void,
      MarketGasPriceTypesRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'MarketGasPriceTypes',
        { type: 'MarketGasPriceTypes', id },
      ],
    }),
  }),
});

export const {
  useGetMarketGasPriceTypesByIdQuery,
  useCreateMarketGasPriceTypesMutation,
  useUpdateMarketGasPriceTypesMutation,
} = marketGasPriceTypesApi;

export const useGetMarketGasPriceTypesInfiniteQuery =
  normalizeInfiniteQueryData<MarketGasPriceType>(
    marketGasPriceTypesApi.useGetMarketGasPriceTypesIInfiniteQuery,
  );
